/**
 * Services API — Supabase PostgREST queries.
 */

import supabase from "../config/supabase";
import { relatedSearchTerms } from "../utils/categorySearch";

const LISTING_SELECT = `
  *,
  categories:service_categories(category_id, description, categories(*)),
  locations:service_locations(location_id, locations(*)),
  user:users!services_user_id_fkey(id, first_name, last_name, profile_image_url, role, is_placeholder, is_test),
  business:businesses(id, name, abn, business_address, is_verified)
`;

const invokeAiModeration = async (body) => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) return;
  supabase.functions.invoke("ai-moderation", {
    body,
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
};

// Write descriptions on the service/category link, never on the shared category.
// Add/update links first, then remove deselected types so failed writes do not
// erase existing descriptions. Callers omitting descriptions preserve them.
async function saveServiceTypes(serviceId, slugs, descriptions) {
  const uniqueSlugs = [...new Set(slugs)];
  if (!uniqueSlugs.length) {
    const {error} = await supabase.from("service_categories").delete().eq("service_id", serviceId);
    return error;
  }
  const {data: categories, error: lookupError} = await supabase.from("categories")
    .select("id, slug").in("slug", uniqueSlugs);
  if (lookupError) return lookupError;
  if (categories?.length !== uniqueSlugs.length) return {message: "Some service types are unavailable. Please choose them again."};
  let existing = [];
  if (descriptions === undefined) {
    const {data, error} = await supabase.from("service_categories")
      .select("category_id, description").eq("service_id", serviceId);
    if (error) return error;
    existing = data || [];
  }
  const rows = categories.map(category => ({
    service_id: serviceId,
    category_id: category.id,
    description: descriptions === undefined
      ? existing.find(link => link.category_id === category.id)?.description || null
      : String(descriptions?.[category.slug] || "").trim().slice(0, 100) || null,
  }));
  const {error: writeError} = await supabase.from("service_categories")
    .upsert(rows, {onConflict: "service_id,category_id"});
  if (writeError) return writeError;
  const {error} = await supabase.from("service_categories").delete().eq("service_id", serviceId)
    .not("category_id", "in", `(${categories.map(category => category.id).join(",")})`);
  return error;
}

// Resolve the set of service IDs located in any of the given location names.
async function serviceIdsForLocationNames(names) {
  const { data: locRows } = await supabase.from("locations").select("id").in("name", names);
  const locIds = (locRows || []).map((l) => l.id);
  if (!locIds.length) return [];
  const { data: rows } = await supabase
    .from("service_locations")
    .select("service_id")
    .in("location_id", locIds);
  return [...new Set((rows || []).map((r) => r.service_id))];
}

// Resolve the set of service IDs in any of the given category slugs.
async function serviceIdsForCategorySlugs(slugs) {
  const { data: catRows } = await supabase.from("categories").select("id").in("slug", slugs);
  const catIds = (catRows || []).map((c) => c.id);
  if (!catIds.length) return [];
  const { data: rows } = await supabase
    .from("service_categories")
    .select("service_id")
    .in("category_id", catIds);
  return [...new Set((rows || []).map((r) => r.service_id))];
}

const servicesApi = {
  async list(params = {}) {
    const search = (params.search || "").trim().slice(0, 120);
    // Home-town browsing: show businesses located IN the town first, then those
    // in nearby towns that service it ("services Glenwood"). Public browse only.
    if (params.nearby && params.location && !params.user_id && !params.savedOnly) {
      const primaryIds = await serviceIdsForLocationNames(params.location.split("|"));
      const primarySet = new Set(primaryIds);
      const nearbyIds = (await serviceIdsForLocationNames(params.nearby.split("|")))
        .filter((id) => !primarySet.has(id));

      let orderedPrimary = primaryIds;
      let orderedNearby = nearbyIds;
      if (params.category) {
        const catIds = await serviceIdsForCategorySlugs(params.category.split("|"));
        if (!catIds.length) return { success: true, data: { items: [], total: 0 } };
        const catSet = new Set(catIds);
        orderedPrimary = primaryIds.filter((id) => catSet.has(id));
        orderedNearby = nearbyIds.filter((id) => catSet.has(id));
      }

      if (search) {
        const terms = relatedSearchTerms(search).map(term => term.replace(/[\\"%_*]/g, " ").trim()).filter(Boolean);
        if (!terms.length) return {success: true, data: {items: [], total: 0}};
        const expression = terms.flatMap(term => [`title.ilike."*${term}*"`, `description.ilike."*${term}*"`]).join(",");
        const matches = new Set();
        // Resolve text matches first to avoid sending thousands of location IDs
        // in a request URL. Preserve the existing home/nearby pagination order.
        for (let offset = 0; ; offset += 1000) {
          const {data, error} = await supabase.from("services").select("id")
            .eq("status", "active").or(expression).order("id").range(offset, offset + 999);
          if (error) return {success: false, error: error.message};
          (data || []).forEach(row => matches.add(row.id));
          if ((data || []).length < 1000) break;
        }
        orderedPrimary = orderedPrimary.filter(id => matches.has(id));
        orderedNearby = orderedNearby.filter(id => matches.has(id));
      }

      const ordered = [...orderedPrimary, ...orderedNearby];
      const total = ordered.length;
      if (!total) return { success: true, data: { items: [], total: 0 } };

      // Partition BEFORE paging: otherwise a first page of seeded rows can
      // obscure genuine listings until the member has already scrolled.
      const testIds = new Set();
      for (let offset = 0; ; offset += 1000) {
        const {data, error} = await supabase.from("services").select("id")
          .eq("status", "active").eq("is_test", true).order("id").range(offset, offset + 999);
        if (error) return {success: false, error: error.message};
        (data || []).forEach(row => testIds.add(row.id));
        if ((data || []).length < 1000) break;
      }
      ordered.sort((a, b) => Number(testIds.has(a)) - Number(testIds.has(b)));

      const limit = params.limit || 20;
      const page = params.page || 1;
      const start = (page - 1) * limit;
      const pageIds = ordered.slice(start, start + limit);
      if (!pageIds.length) return { success: true, data: { items: [], total } };

      const { data, error } = await supabase
        .from("services")
        .select(LISTING_SELECT)
        .eq("status", "active")
        .in("id", pageIds);
      if (error) return { success: false, error: error.message };

      const rank = Object.fromEntries(ordered.map((id, i) => [id, i]));
      const items = (data || [])
        .map(flattenService)
        .sort((a, b) => (rank[a.id] ?? 0) - (rank[b.id] ?? 0));
      return { success: true, data: { items, total } };
    }

    let serviceIds = null;
    if (params.savedOnly) {
      const {data: {user}} = await supabase.auth.getUser();
      if (!user) return {success: true, data: {items: [], total: 0}};
      const {data, error} = await supabase.from("saved_services").select("service_id").eq("user_id", user.id);
      if (error) return {success: false, error: "Couldn't load saved services."};
      serviceIds = (data || []).map(row => row.service_id);
      if (!serviceIds.length) return {success: true, data: {items: [], total: 0}};
    }

    // Server-side location filter: get matching service IDs first
    if (params.location) {
      const names = params.location.split("|");
      const { data: locRows } = await supabase
        .from("locations")
        .select("id")
        .in("name", names);
      const locIds = (locRows || []).map((l) => l.id);

      if (!locIds.length) return { success: true, data: { items: [], total: 0 } };

      const { data: svcLocRows } = await supabase
        .from("service_locations")
        .select("service_id")
        .in("location_id", locIds);
      const locationServiceIds = [...new Set((svcLocRows || []).map((r) => r.service_id))];
      serviceIds = serviceIds ? serviceIds.filter(id => locationServiceIds.includes(id)) : locationServiceIds;

      if (!serviceIds.length) return { success: true, data: { items: [], total: 0 } };
    }

    // Server-side category filter
    if (params.category) {
      const slugs = params.category.split("|");
      const { data: catRows } = await supabase
        .from("categories")
        .select("id")
        .in("slug", slugs);
      const catIds = (catRows || []).map((c) => c.id);

      if (!catIds.length) return { success: true, data: { items: [], total: 0 } };

      const { data: svcCatRows } = await supabase
        .from("service_categories")
        .select("service_id")
        .in("category_id", catIds);
      const catServiceIds = [...new Set((svcCatRows || []).map((r) => r.service_id))];

      if (!catServiceIds.length) return { success: true, data: { items: [], total: 0 } };

      // Intersect with location filter if both active
      serviceIds = serviceIds
        ? serviceIds.filter((id) => catServiceIds.includes(id))
        : catServiceIds;

      if (!serviceIds.length) return { success: true, data: { items: [], total: 0 } };
    }

    let query = supabase.from("services").select(LISTING_SELECT, {count: "exact"});
    if (search) {
      const terms = relatedSearchTerms(search).map(term => term.replace(/[\\"%_*]/g, " ").trim()).filter(Boolean);
      if (terms.length) query = query.or(terms.flatMap(term => [
        `title.ilike."*${term}*"`, `description.ilike."*${term}*"`,
      ]).join(","));
      else return {success: true, data: {items: [], total: 0}};
    }

    // Owners see their own listings at any status (so a pending submission shows
    // as "awaiting review" in their view); everyone else sees only active ones.
    // RLS enforces this at the DB layer too — this just keeps the UI consistent.
    if (params.user_id) {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.id === params.user_id) {
        query = query.eq("user_id", params.user_id);
      } else {
        query = query.eq("status", "active").eq("user_id", params.user_id);
      }
    } else {
      query = query.eq("status", "active");
    }

    if (serviceIds) query = query.in("id", serviceIds);
    if (params.listing_type) query = query.eq("listing_type", params.listing_type);

    query = query.order("is_test", {ascending: true}).order("created_at", { ascending: false });

    if (params.limit) query = query.limit(params.limit);
    if (params.page && params.limit) {
      const offset = (params.page - 1) * params.limit;
      query = query.range(offset, offset + params.limit - 1);
    } else if (params.offset) {
      query = query.range(params.offset, params.offset + (params.limit || 20) - 1);
    }

    const { data, error, count } = await query;
    if (error) return { success: false, error: error.message };

    const items = (data || []).map(flattenService);
    return { success: true, data: { items, total: count ?? items.length } };
  },

  // Fallback browse: matching services located OUTSIDE the home+ring catchment.
  // Used only when an in-ring search returns nothing, so users still get a
  // result ("Also further afield") for rare services that only exist further
  // away. Requires a category (we don't return the entire out-of-ring set).
  async listOutOfRing(params = {}) {
    const ringNames = params.ring || [];
    if (!params.category || !ringNames.length) {
      return { success: true, data: { items: [] } };
    }
    const ringIds = new Set(await serviceIdsForLocationNames(ringNames));
    const catIds = await serviceIdsForCategorySlugs(params.category.split("|"));
    const candidateIds = catIds.filter((id) => !ringIds.has(id));
    if (!candidateIds.length) return { success: true, data: { items: [] } };

    const { data, error } = await supabase
      .from("services")
      .select(LISTING_SELECT)
      .eq("status", "active")
      .in("id", candidateIds.slice(0, 100))
      // Recommendations first, then the title, which is stable and predictable.
      // This used to order by rating_average, which for a scraped business holds
      // *Google's* rating — so the thing surfacing "the good ones" out here was
      // Google's data, the first item in 5_LEGAL_EXPOSURE.md. See
      // 6_REVIEWS_REQUIREMENT.md and docs/SERVICE_RANKING.md §5.
      .order("is_test", {ascending: true})
      .order("recommendation_count", { ascending: false })
      .order("title", { ascending: true })
      .limit(params.limit || 20);
    if (error) return { success: false, error: error.message };
    return { success: true, data: { items: (data || []).map(flattenService) } };
  },

  async get(serviceId) {
    const { data, error } = await supabase
      .from("services")
      .select(LISTING_SELECT)
      .eq("id", serviceId)
      .single();
    if (error) return { success: false, error: error.message };
    return { success: true, data: flattenService(data) };
  },

  async create(body) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const {
      categories: catSlugsA,
      category_slugs: catSlugsB,
      locations: locNamesA,
      location_names: locNamesB,
      category_descriptions,
      ...serviceData
    } = body;
    const catSlugs = catSlugsA || catSlugsB;
    const locNames = locNamesA || locNamesB;
    if (catSlugs?.some(slug => !String(category_descriptions?.[slug] || "").trim())) {
      return {success: false, error: "Add a description for every selected service type."};
    }
    serviceData.user_id = user.id;

    // Admins/moderators skip the queue — go straight to active
    const { data: profile } = await supabase.from("users").select("role").eq("id", user.id).single();
    serviceData.status = (profile?.role === "admin" || profile?.role === "moderator") ? "active" : "pending";

    const { data, error } = await supabase.from("services").insert(serviceData).select().single();
    if (error) return { success: false, error: error.message };

    if (catSlugs) {
      const typeError = await saveServiceTypes(data.id, catSlugs, category_descriptions);
      if (typeError) return {success: false, error: `Service saved, but service types could not be saved: ${typeError.message}`};
    }

    // Link locations
    if (locNames?.length) {
      const { data: locs } = await supabase.from("locations").select("id").in("name", locNames);
      if (locs?.length) {
        await supabase.from("service_locations").insert(
          locs.map((l) => ({ service_id: data.id, location_id: l.id }))
        );
      }
    }

    if (data.status === "pending") {
      invokeAiModeration({
          item_id: data.id,
          item_type: "service",
          title: data.title,
          description: data.description,
      });
      
      // Admin app/phone alerts are created by the database trigger (065).
    }

    return { success: true, data };
  },

  async update(serviceId, body) {
    const {
      categories: catSlugsA,
      category_slugs: catSlugsB,
      locations: locNamesA,
      location_names: locNamesB,
      category_descriptions,
      ...rest
    } = body;
    const catSlugs = catSlugsA || catSlugsB;
    const locNames = locNamesA || locNamesB;
    if (category_descriptions !== undefined && catSlugs?.some(slug => !String(category_descriptions?.[slug] || "").trim())) {
      return {success: false, error: "Add a description for every selected service type."};
    }
    
    // Only include valid services table fields that can be updated
    const updateData = {};
    const validFields = ['title', 'description', 'exchange_type', 'listing_type', 'status', 
                         'abn', 'business_address', 'landline', 'mobile', 'email', 'opening_hours'];
    validFields.forEach(field => {
      if (rest[field] !== undefined) {
        updateData[field] = rest[field];
      }
    });
    
    const { data, error } = await supabase
      .from("services")
      .update(updateData)
      .eq("id", serviceId)
      .select()
      .single();
    if (error) return { success: false, error: error.message };

    if (catSlugs) {
      const typeError = await saveServiceTypes(serviceId, catSlugs, category_descriptions);
      if (typeError) return {success: false, error: `Service saved, but service types could not be saved: ${typeError.message}`};
    }

    // Sync service-area links (replace set) when provided. This is how a claimed
    // owner's "Areas I service" selection persists, feeding location search.
    if (locNames) {
      await supabase.from("service_locations").delete().eq("service_id", serviceId);
      if (locNames.length) {
        const { data: locs } = await supabase.from("locations").select("id").in("name", locNames);
        if (locs?.length) {
          await supabase.from("service_locations").insert(
            locs.map((l) => ({ service_id: serviceId, location_id: l.id }))
          );
        }
      }
    }

    return { success: true, data };
  },

  async submit(serviceId) {
    const { data: svc, error } = await supabase
      .from("services")
      .select("status, title, description")
      .eq("id", serviceId)
      .single();
    if (error) return { success: false, error: error.message };
    if (svc?.status !== "pending") return { success: true };

    // Trigger AI moderation asynchronously. The DB safety-net trigger already
    // created a queue row; AI only enriches it with a recommendation.
    if (svc) {
      invokeAiModeration({ item_id: serviceId, item_type: "service" });
    }

    return { success: true };
  },

  async delete(serviceId) {
    const { error } = await supabase.from("services").delete().eq("id", serviceId);
    if (error) return { success: false, error: error.message };
    return { success: true };
  },
};

function flattenService(row) {
  if (!row) return row;
  return {
    ...row,
    categories: (row.categories || []).map((jt) => jt.categories
      ? {...jt.categories, description: jt.description || ""} : null).filter(Boolean),
    locations: (row.locations || []).map((jt) => jt.locations).filter(Boolean),
  };
}

export default servicesApi;
